import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ChevronRight, Loader2, Dog,
  Mountain, Shield, Flame, ArrowLeftRight, Snowflake,
  Search, Crosshair, Waves, Heart, Wind, Sparkles,
} from 'lucide-react';
import MarketplaceFooter from '@/components/MarketplaceFooter';
import TopNavBar from '@/components/TopNavBar';
import { API } from '@/utils/api';

const ICONS = {
  Mountain, Shield, Flame, ArrowLeftRight, Snowflake,
  Search, Crosshair, Waves, Heart, Wind, Sparkles,
};

export default function FciGroupsIndex() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/grupy-fci`)
      .then(res => setGroups(res.data.groups || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#05050f] text-white">
      <Helmet>
        <title>Grupy FCI — systematyka ras psów rasowych | ApisAI</title>
        <meta name="description" content="Przeglądaj hodowle psów rasowych według 10 grup FCI. Owczarki, teriery, molosy, jamniki, szpice, gończe, wyżły, aportery, psy do towarzystwa i charty." />
        <meta property="og:title" content="Grupy FCI — systematyka ras psów rasowych" />
        <meta property="og:description" content="Wszystkie rasy psów uznane przez FCI podzielone na 10 grup wg przeznaczenia użytkowego." />
        <link rel="canonical" href="https://apisai.pl/grupy-fci" />
      </Helmet>

      <TopNavBar />

      {/* Hero */}
      <section className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <Link to="/katalog" className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1 mb-6" data-testid="fci-back-to-catalog">
            ← Wróć do marketplace
          </Link>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="fci-groups-title">
            Grupy <span className="text-emerald-400">FCI</span>
          </h1>
          <p className="text-base text-gray-400 mt-4 max-w-2xl">
            Międzynarodowa Federacja Kynologiczna (FCI) dzieli wszystkie uznane rasy psów na
            <strong className="text-white"> 10 grup</strong> według ich użytkowości — celu dla którego zostały wyhodowane.
            Znajdź hodowle psów rasowych FCI / ZKwP według grupy.
          </p>
        </div>
      </section>

      {/* Groups grid */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-20"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="fci-groups-grid">
            {groups.map((g, i) => {
              const IconComponent = ICONS[g.icon] || Dog;
              return (
              <motion.div
                key={g.slug}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <Link
                  to={`/grupy-fci/${g.slug}`}
                  data-testid={`fci-group-card-${g.slug}`}
                  className="group relative block bg-[#0c0c1a]/80 border border-white/10 rounded-xl p-5 hover:border-emerald-400/40 hover:bg-[#0c0c1a] transition-all h-full overflow-hidden"
                >
                  {/* Subtle gradient glow on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/0 via-emerald-500/0 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

                  <div className="relative flex items-start justify-between mb-3">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-400/20 flex items-center justify-center shrink-0 group-hover:from-emerald-500/30 group-hover:to-emerald-500/10 transition-all">
                        <IconComponent className="w-6 h-6 text-emerald-400" strokeWidth={1.5} />
                      </div>
                      <div>
                        <div className="text-[10px] text-emerald-400/80 font-mono tracking-[0.15em] uppercase">
                          {g.roman} Grupa FCI
                        </div>
                        <h2 className="text-lg font-bold text-white group-hover:text-emerald-400 transition-colors leading-tight mt-0.5">
                          {g.name}
                        </h2>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-emerald-400 group-hover:translate-x-0.5 transition-all mt-2" />
                  </div>

                  <p className="relative text-xs text-gray-400 leading-relaxed line-clamp-3 mb-4">
                    {g.description}
                  </p>

                  <div className="relative flex items-center gap-3 pt-3 border-t border-white/5">
                    <span className="text-[11px] text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                      <Dog className="w-3 h-3" />
                      <span className="text-white font-semibold">{g.breeds_count}</span> ras
                    </span>
                  </div>
                </Link>
              </motion.div>
              );
            })}
          </div>
        )}
      </section>

      <MarketplaceFooter />
    </div>
  );
}
